import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  LinearProgress,
  alpha,
  useTheme
} from '@mui/material';
import {
  StorefrontOutlined,
  CalculateOutlined,
  CheckCircleOutline,
  LockOutlined,
  ArrowForward
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const OnboardingStepCard = ({
  title,
  description,
  icon,
  isComplete,
  isLocked,
  isActive,
  buttonLabel,
  onAction
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={isActive ? 3 : 1}
      sx={{
        p: 4,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: isActive ? `2px solid ${theme.palette.primary.main}` : '1px solid',
        borderColor: isActive ? 'primary.main' : 'divider',
        opacity: isLocked ? 0.6 : 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: !isLocked ? theme.shadows[4] : undefined
        }
      }}
    >
      {/* Status chip */}
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        {isComplete ? (
          <Chip
            icon={<CheckCircleOutline />}
            label="Complete"
            color="success"
            size="small"
          />
        ) : isLocked ? (
          <Chip
            icon={<LockOutlined />}
            label="Locked"
            size="small"
            variant="outlined"
          />
        ) : isActive ? (
          <Chip
            label="Current Step"
            color="primary"
            size="small"
          />
        ) : null}
      </Box>

      {/* Icon */}
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isComplete
            ? alpha(theme.palette.success.main, 0.1)
            : isActive
            ? alpha(theme.palette.primary.main, 0.1)
            : alpha(theme.palette.grey[500], 0.1),
          mb: 3
        }}
      >
        {React.cloneElement(icon, {
          sx: {
            fontSize: 32,
            color: isComplete
              ? 'success.main'
              : isActive
              ? 'primary.main'
              : 'grey.500'
          }
        })}
      </Box>

      {/* Content */}
      <Typography variant="h5" gutterBottom fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
        {description}
      </Typography>

      {/* Action button */}
      <Button
        variant={isActive ? 'contained' : 'outlined'}
        size="large"
        fullWidth
        disabled={isLocked || isComplete}
        onClick={onAction}
        endIcon={<ArrowForward />}
        sx={{ mt: 'auto' }}
      >
        {isComplete ? 'Completed' : buttonLabel}
      </Button>
    </Paper>
  );
};

const GetStartedPage = () => {
  const navigate = useNavigate();
  const {
    user,
    getUserBrands,
    getBrandPlanStatuses,
    isOnboardingComplete
  } = useAuth();
  const theme = useTheme();

  const brands = getUserBrands();
  const brandPlanStatuses = getBrandPlanStatuses();
  const hasBrand = brands.length > 0;
  const hasCompletePlan = brandPlanStatuses.some(b => b.isComplete);

  // Determine current step
  let currentStep = 1;
  if (hasBrand && !hasCompletePlan) currentStep = 2;
  if (hasCompletePlan) currentStep = 3;

  // Get the first brand ID for navigation
  const firstBrandId = brands[0]?.brandId?._id || brands[0]?.brandId;

  // Progress percentage
  const progressPercent = hasCompletePlan ? 100 : hasBrand ? 50 : 0;

  const handleAddBrand = () => {
    navigate('/brands/new');
  };

  const handleBuildPlan = () => {
    if (firstBrandId) {
      // Navigate to brand dashboard with planning tab
      navigate(`/brands/${firstBrandId}?tab=planning`);
    }
  };

  const handleGoToDashboard = () => {
    if (firstBrandId) {
      navigate(`/brands/${firstBrandId}`);
    } else {
      navigate('/dashboard');
    }
  };

  // If onboarding is complete, redirect to dashboard
  if (isOnboardingComplete()) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', bgcolor: 'success.light', borderRadius: 3 }}>
          <CheckCircleOutline sx={{ fontSize: 64, color: 'success.dark', mb: 2 }} />
          <Typography variant="h4" gutterBottom fontWeight={600}>
            You're All Set!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Your brand and financial plan are ready. Start creating amazing products!
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleGoToDashboard}
            endIcon={<ArrowForward />}
          >
            Go to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" gutterBottom fontWeight={700}>
            Welcome to Studio Sivad
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            Let's get your brand set up. Complete these two steps to unlock the full platform.
          </Typography>

          {/* Progress bar */}
          <Box sx={{ maxWidth: 400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Setup Progress
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                Step {currentStep > 2 ? 2 : currentStep} of 2
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4
                }
              }}
            />
          </Box>
        </Box>

        {/* Steps */}
        <Grid container spacing={4} justifyContent="center">
          {/* Step 1: Add Brand */}
          <Grid item xs={12} md={5}>
            <OnboardingStepCard
              title="Add Your Brand"
              description="Create your first brand to start managing products, campaigns, and manufacturing."
              icon={<StorefrontOutlined />}
              isComplete={hasBrand}
              isLocked={false}
              isActive={!hasBrand}
              buttonLabel="Add a Brand"
              onAction={handleAddBrand}
            />
          </Grid>

          {/* Step 2: Build Financial Plan */}
          <Grid item xs={12} md={5}>
            <OnboardingStepCard
              title="Build Financial Plan"
              description="Set your revenue goals, margin targets, and product mix to unlock the full platform."
              icon={<CalculateOutlined />}
              isComplete={hasCompletePlan}
              isLocked={!hasBrand}
              isActive={hasBrand && !hasCompletePlan}
              buttonLabel="Build Your Plan"
              onAction={handleBuildPlan}
            />
          </Grid>
        </Grid>

        {/* Help text */}
        {hasBrand && !hasCompletePlan && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                maxWidth: 600,
                mx: 'auto',
                bgcolor: alpha(theme.palette.info.main, 0.08),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                borderRadius: 2
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Your brand <strong>{brands[0]?.brandId?.name || 'has been created'}</strong>!
                Now complete your financial plan to set revenue goals and unlock all features.
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography variant="body2" color="text.secondary">
            Need help? Contact us at{' '}
            <a href="mailto:support@studiosivad.com" style={{ color: theme.palette.primary.main }}>
              support@studiosivad.com
            </a>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default GetStartedPage;
